import { useState } from "react";
import { getAgentIdIfPresent } from "../../auth/auth";
import { useAuthContext } from "../../auth/AuthContext";
import { type ClientFilters, emptyClientFilters } from "./clientFilters";
import { removeFilterValue } from "./filters/activeFilterChipsHelper";

interface UseClientFiltersOptions {
  readonly onChange: () => void;
}

interface UseClientFilters {
  readonly appliedFilters: ClientFilters;
  readonly applyFilters: (filters: ClientFilters) => void;
  readonly resetFilters: () => void;
  readonly removeFilter: (key: keyof ClientFilters, value: string) => void;
}

/** Filters start narrowed to the logged-in agent's customers; resetting clears every filter. */
export function useClientFilters({ onChange }: UseClientFiltersOptions): UseClientFilters {
  const { context } = useAuthContext();
  const [appliedFilters, setAppliedFilters] = useState<ClientFilters>(() => ({
    ...emptyClientFilters,
    accountManagerId: getAgentIdIfPresent(context) ?? "",
  }));

  const applyFilters = (filters: ClientFilters) => {
    setAppliedFilters({ ...filters });
    onChange();
  };

  const resetFilters = () => {
    // A fresh object, so the filter form discards its unapplied draft even when nothing was applied yet.
    setAppliedFilters({ ...emptyClientFilters });
    onChange();
  };

  const removeFilter = (key: keyof ClientFilters, value: string) => {
    setAppliedFilters(removeFilterValue(appliedFilters, key, value));
    onChange();
  };

  return { appliedFilters, applyFilters, resetFilters, removeFilter };
}
